import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  SongProject,
  CustomInstrument,
  DrumKit,
  RecordedSample,
} from '../types/music';

export type CompositionType = 'keyboard' | 'beat' | 'guitar' | 'song' | 'vocal' | 'sample';

/** Phase 4A — keyboard pedalboard state. Single shared config across all
 *  4 base instruments; per-instrument overrides land with the Phase 4B
 *  Synth Lab where each saved CustomInstrument carries its own FX. */
export interface KeyboardEffects {
  reverb:     { on: boolean; wet: number; decay: number };
  delay:      { on: boolean; wet: number; time: '8n' | '16n' | '4n'; feedback: number };
  distortion: { on: boolean; amount: number };
  chorus:     { on: boolean; wet: number; depth: number; rate: number };
}

export const DEFAULT_KEYBOARD_EFFECTS: KeyboardEffects = {
  reverb:     { on: false, wet: 0.3, decay: 1.8 },
  delay:      { on: false, wet: 0.25, time: '8n', feedback: 0.3 },
  distortion: { on: false, amount: 0.4 },
  chorus:     { on: false, wet: 0.4, depth: 0.6, rate: 1.5 },
};

export interface Composition {
  id: string;
  name: string;
  type: CompositionType;
  data: unknown; // beat grid pattern, recorded note events, song project ref, vocal blob ref, etc.
  createdAt: string;
  childId: string;
  favorite: boolean;
  /** Optional metadata used by Creations sort/filter and the Phase 8 cross-app
   *  hook. All optional for back-compat with v0 records. */
  tags?: string[];
  bpm?: number;
  durationMs?: number;
  /** For type='song' bounces, the SongProject this came from. */
  sourceProjectId?: string;
}

export interface RecordedNote {
  note: string;
  time: number; // ms offset from recording start
  duration: number;
  instrument: string;
}

interface MusicState {
  // ─── Compositions (existing) ───
  compositions: Composition[];

  // ─── Song projects (Phase 2 looper / song builder) ───
  songProjects: SongProject[];

  // ─── User-created assets ───
  customInstruments: CustomInstrument[];   // Phase 4 Synth Lab outputs
  customKits: DrumKit[];                   // Phase 3 saved drum kits
  recordedSamples: RecordedSample[];       // Phase 10 sample-your-own-drum

  // ─── Studio prefs ───
  volume: number;
  colorMode: boolean;
  showNoteLabels: boolean;
  metronomeEnabled: boolean;
  keyboardEffects: KeyboardEffects;
  lastBeatBpm: number;
  lastLoopBpm: number;

  // ─── Composition CRUD ───
  saveComposition: (comp: Omit<Composition, 'id' | 'createdAt' | 'favorite'>) => string;
  deleteComposition: (id: string) => void;
  toggleFavorite: (id: string) => void;
  renameComposition: (id: string, name: string) => void;
  getCompositionsForChild: (childId: string) => Composition[];

  // ─── SongProject CRUD ───
  saveSongProject: (project: Omit<SongProject, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateSongProject: (id: string, updates: Partial<Omit<SongProject, 'id' | 'createdAt' | 'childId'>>) => void;
  deleteSongProject: (id: string) => void;
  getSongProjectsForChild: (childId: string) => SongProject[];

  // ─── Custom instruments (Synth Lab) ───
  saveCustomInstrument: (inst: Omit<CustomInstrument, 'id' | 'createdAt'>) => string;
  deleteCustomInstrument: (id: string) => void;
  getCustomInstrumentsForChild: (childId: string) => CustomInstrument[];

  // ─── Custom drum kits ───
  saveCustomKit: (kit: Omit<DrumKit, 'id' | 'builtin'>) => string;
  deleteCustomKit: (id: string) => void;
  getCustomKitsForChild: (childId: string) => DrumKit[];

  // ─── Recorded samples ───
  addRecordedSample: (sample: Omit<RecordedSample, 'id' | 'createdAt'>) => string;
  deleteRecordedSample: (id: string) => void;
  getRecordedSamplesForChild: (childId: string) => RecordedSample[];

  // ─── Prefs setters ───
  setVolume: (v: number) => void;
  setColorMode: (on: boolean) => void;
  setShowNoteLabels: (on: boolean) => void;
  setMetronomeEnabled: (on: boolean) => void;
  setKeyboardEffects: (next: KeyboardEffects | ((prev: KeyboardEffects) => KeyboardEffects)) => void;
  setLastBeatBpm: (bpm: number) => void;
  setLastLoopBpm: (bpm: number) => void;
}

const MAX_NAME = 80;
const cap = (s: string, n: number) => (typeof s === 'string' ? s.slice(0, n) : '');

export const useMusicStore = create<MusicState>()(
  persist(
    (set, get) => ({
      compositions: [],
      songProjects: [],
      customInstruments: [],
      customKits: [],
      recordedSamples: [],

      volume: 0.7,
      colorMode: true,
      showNoteLabels: true,
      metronomeEnabled: false,
      keyboardEffects: { ...DEFAULT_KEYBOARD_EFFECTS },
      lastBeatBpm: 120,
      lastLoopBpm: 100,

      // ── Compositions ──

      saveComposition: (comp) => {
        const id = crypto.randomUUID();
        const full: Composition = {
          ...comp,
          name: cap(comp.name, MAX_NAME).trim() || 'Untitled',
          id,
          createdAt: new Date().toISOString(),
          favorite: false,
        };
        set((s) => ({ compositions: [...s.compositions, full] }));
        return id;
      },

      deleteComposition: (id) => {
        set((s) => ({ compositions: s.compositions.filter((c) => c.id !== id) }));
      },

      toggleFavorite: (id) => {
        set((s) => ({
          compositions: s.compositions.map((c) =>
            c.id === id ? { ...c, favorite: !c.favorite } : c
          ),
        }));
      },

      renameComposition: (id, name) => {
        const safe = cap(name, MAX_NAME).trim();
        if (!safe) return;
        set((s) => ({
          compositions: s.compositions.map((c) =>
            c.id === id ? { ...c, name: safe } : c
          ),
        }));
      },

      getCompositionsForChild: (childId) => {
        return get().compositions.filter((c) => c.childId === childId);
      },

      // ── Song projects ──

      saveSongProject: (project) => {
        const id = crypto.randomUUID();
        const now = new Date().toISOString();
        const full: SongProject = {
          ...project,
          name: cap(project.name, MAX_NAME).trim() || 'Untitled Song',
          id,
          createdAt: now,
          updatedAt: now,
        };
        set((s) => ({ songProjects: [...s.songProjects, full] }));
        return id;
      },

      updateSongProject: (id, updates) => {
        set((s) => ({
          songProjects: s.songProjects.map((p) =>
            p.id === id
              ? {
                  ...p,
                  ...updates,
                  ...(typeof updates.name === 'string'
                    ? { name: cap(updates.name, MAX_NAME).trim() || p.name }
                    : {}),
                  updatedAt: new Date().toISOString(),
                }
              : p
          ),
        }));
      },

      deleteSongProject: (id) => {
        set((s) => ({ songProjects: s.songProjects.filter((p) => p.id !== id) }));
      },

      getSongProjectsForChild: (childId) => {
        return get().songProjects.filter((p) => p.childId === childId);
      },

      // ── Custom instruments ──

      saveCustomInstrument: (inst) => {
        const id = crypto.randomUUID();
        const full: CustomInstrument = {
          ...inst,
          name: cap(inst.name, MAX_NAME).trim() || 'My Sound',
          id,
          createdAt: new Date().toISOString(),
        };
        set((s) => ({ customInstruments: [...s.customInstruments, full] }));
        return id;
      },

      deleteCustomInstrument: (id) => {
        set((s) => ({ customInstruments: s.customInstruments.filter((i) => i.id !== id) }));
      },

      getCustomInstrumentsForChild: (childId) => {
        return get().customInstruments.filter((i) => i.childId === childId);
      },

      // ── Custom kits ──

      saveCustomKit: (kit) => {
        const id = crypto.randomUUID();
        const full: DrumKit = {
          ...kit,
          name: cap(kit.name, MAX_NAME).trim() || 'My Kit',
          id,
          builtin: false,
        };
        set((s) => ({ customKits: [...s.customKits, full] }));
        return id;
      },

      deleteCustomKit: (id) => {
        set((s) => ({ customKits: s.customKits.filter((k) => k.id !== id) }));
      },

      getCustomKitsForChild: (childId) => {
        return get().customKits.filter((k) => k.childId === childId);
      },

      // ── Recorded samples ──

      addRecordedSample: (sample) => {
        const id = crypto.randomUUID();
        const full: RecordedSample = {
          ...sample,
          name: cap(sample.name, MAX_NAME).trim() || 'My Sample',
          id,
          createdAt: new Date().toISOString(),
        };
        set((s) => ({ recordedSamples: [...s.recordedSamples, full] }));
        return id;
      },

      deleteRecordedSample: (id) => {
        set((s) => ({ recordedSamples: s.recordedSamples.filter((r) => r.id !== id) }));
      },

      getRecordedSamplesForChild: (childId) => {
        return get().recordedSamples.filter((r) => r.childId === childId);
      },

      // ── Prefs ──

      setVolume: (v) => set({ volume: v }),
      setColorMode: (on) => set({ colorMode: on }),
      setShowNoteLabels: (on) => set({ showNoteLabels: on }),
      setMetronomeEnabled: (on) => set({ metronomeEnabled: on }),
      setKeyboardEffects: (next) => set((s) => ({
        keyboardEffects: typeof next === 'function' ? next(s.keyboardEffects) : next,
      })),
      setLastBeatBpm: (bpm) => set({ lastBeatBpm: bpm }),
      setLastLoopBpm: (bpm) => set({ lastLoopBpm: bpm }),
    }),
    {
      name: 'homeschool-music',
      version: 2,
      migrate: (persisted, version) => {
        const state = persisted as Partial<MusicState>;
        if (version < 1) {
          // v0 → v1: add empty arrays for new collections + metronome pref.
          // Existing `compositions` are untouched; v0 records remain valid.
          state.songProjects = state.songProjects ?? [];
          state.customInstruments = state.customInstruments ?? [];
          state.customKits = state.customKits ?? [];
          state.recordedSamples = state.recordedSamples ?? [];
          state.metronomeEnabled = state.metronomeEnabled ?? false;
        }
        if (version < 2) {
          // v1 → v2: keyboard pedalboard effects (Phase 4A). All toggles
          // default off so existing keyboards sound exactly like before.
          state.keyboardEffects = state.keyboardEffects ?? { ...DEFAULT_KEYBOARD_EFFECTS };
          state.lastBeatBpm = state.lastBeatBpm ?? 120;
          state.lastLoopBpm = state.lastLoopBpm ?? 100;
        }
        return state as unknown as MusicState;
      },
    }
  )
);
